import { Component, inject, Inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatError, MatInput, MatSuffix } from '@angular/material/input';
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
} from '../../../constants/date.constants';
import moment from 'moment';
import { SingleActionDialogComponent } from '../single-action-dialog/single-action-dialog.component';
import { MaskEmailPipe } from '../../../../pipes/mask-email.pipe';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DateInputDirective } from '../../../../directives/date-input.directive';
import { AlphaNumericOnlyDirective } from '../../../../directives/alpha-numeric-only.directive';
import { SetPasswordService } from '../../../services/set-password.service';
import { Router } from '@angular/router';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { CreateMemberRequest } from '../../../interfaces/set-password.model';
import { STORAGE_KEY } from '../../../constants/store-key.constants';
import { LOGIN_REGISTER_TABS } from '../../../constants/common.constants';
import {
  birthdayValidator,
  idNoValidator,
} from '../../../utils/validator.util';

@Component({
  selector: 'app-forgot-password-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    FormsModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatInput,
    ReactiveFormsModule,
    MatError,
    NgClass,
    MatButton,
    DateInputDirective,
    AlphaNumericOnlyDirective,
  ],
  templateUrl: './forgot-password-dialog.component.html',
  styleUrl: './forgot-password-dialog.component.scss',
})
export class ForgotPasswordDialogComponent {
  constructor(public dialogRef: MatDialogRef<ForgotPasswordDialogComponent>) {}

  dialog: MatDialog = inject(MatDialog);
  translateService: TranslateService = inject(TranslateService);
  setPasswordService: SetPasswordService = inject(SetPasswordService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);
  maskEmailPipe = new MaskEmailPipe();
  showErrorMessage: boolean = false; // 是否顯示錯誤訊息

  idNumberMaxLength: number = 10; // 身分證號最大長度

  forgotPasswordForm = new FormGroup({
    birthday: new FormControl<moment.Moment | null>(null, [
      Validators.required,
      (control: FormControl) =>
        birthdayValidator(control, this.translateService, {
          max: 100,
          min: 18,
        }),
    ]),
    idNumber: new FormControl('', [
      Validators.required,
      (control: FormControl) => idNoValidator(control, this.translateService),
    ]),
  });

  // 獲取驗證生日錯誤訊息
  getBirthdayErrorMessage() {
    const control = this.forgotPasswordForm.get('birthday');
    if (!control || !control.errors || !this.showErrorMessage) return '';

    const errors = control.errors;

    // mat-datepickerParse錯誤
    if (errors['matDatepickerParse']) {
      return this.translateService.instant('error.birthday-error.format');
    }
    if (errors['required']) {
      return this.translateService.instant('error.birthday-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 取得身份證號錯誤訊息
  getIdNumberErrorMessage() {
    const control = this.forgotPasswordForm.get('idNumber');
    if (!control || !control.errors || !this.showErrorMessage) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.id-number-error.required');
    }
    if (errors['invalidCharacters']) {
      return errors['invalidCharacters'];
    }
  }

  // 關閉當前dialog
  closeDialog() {
    this.dialogRef.close();
  }

  onSubmit() {
  this.showErrorMessage = true;

  if (this.forgotPasswordForm.valid) {
    this.showErrorMessage = false;

    const birthday =
      this.forgotPasswordForm
        .get('birthday')
        ?.value?.format(API_DATE_FORMAT) || '';
    const idNo = this.forgotPasswordForm.get('idNumber')?.value || '';
    const request = { birthday, idNo };
    
    this.setPasswordService.sendForgetPassword(request).subscribe({
      next: (res) => {
        // if find match, send reset password link to register email
        if (res.statusCode === 'S0000') {
          this.dialog.open(SingleActionDialogComponent, {
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'forgot-password-dialog.already-send-email-title',
                { email: this.maskEmailPipe.transform(res.data?.email || '') }
              ),
              message: this.translateService.instant(
                'forgot-password-dialog.already-send-email-message'
              ),
              showCloseButton: false,
              buttonText: this.translateService.instant(
                'common.button.confirm'
              ),
              action: () => this.dialog.closeAll(),
            },
          });
        } else if (res.statusCode === 'E9011') {
          this.dialog.open(SingleActionDialogComponent, {
            // if this id isn't found in system let user go to register
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'forgot-password-dialog.not-register-title'
              ),
              message: this.translateService.instant(
                'forgot-password-dialog.not-register-message'
              ),
              showCloseButton: true,
              buttonText: this.translateService.instant(
                'register-verify-contact.register'
              ),
              action: () => {
                // being nice put birthday and id directly fill in register form
                const createMemberRequest: CreateMemberRequest = {
                  gender: '',
                  birthday,
                  name: '',
                  idNo,
                  cell: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                };
                this.sharedStorageService.set(
                  STORAGE_KEY.CREATE_MEMBER_REQUEST,
                  createMemberRequest
                );

                this.router
                  .navigateByUrl('/healthcheck', { skipLocationChange: true })
                  .then(() => {
                    this.router
                      .navigate(['/healthcheck/login'], {
                        queryParams: { tab: LOGIN_REGISTER_TABS.REGISTER },
                      })
                      .then(() => this.dialog.closeAll());
                  });
              },
            },
          });
        } else if (res.statusCode === 'EC1004') {
            this.dialog.open(SingleActionDialogComponent, {
              disableClose: true,
              data: {
                title: this.translateService.instant(
                  'forgot-password-dialog.not-match-title'
                ),
                message: this.translateService.instant(
                  'forgot-password-dialog.not-match-message'  
                ),
                showCloseButton: false,
                buttonText: this.translateService.instant(
                  'common.button.confirm'
                ),
                action: () => this.dialog.closeAll(),
              },
            });
          }else {
          const noMatchDialog = this.dialog.open(SingleActionDialogComponent, {
            // if id and birth doesn't match, just pop up dialog
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'forgot-password-dialog.not-match-title'
              ),
              message: this.translateService.instant(
                'forgot-password-dialog.not-match-message'
              ),
              showCloseButton: false,
              buttonText: this.translateService.instant(
                'common.button.confirm'
              ),
              action: () => noMatchDialog.close(),
            },
          });
        }
      },
      error: () => {
        // if send email failed, pop up dialog
        this.dialog.open(SingleActionDialogComponent, {
          disableClose: true,
          data: {
            title: this.translateService.instant(
              'forgot-password-dialog.send-email-failed-title'
            ),
            message: this.translateService.instant(
              'forgot-password-dialog.send-email-failed-message'
            ),
            showCloseButton: false,
            buttonText: this.translateService.instant(
              'common.button.confirm'
            ),
            action: () => this.dialog.closeAll(),
          },
        });
      }
    });
  }
}

  protected readonly DATE_FORMAT = DATE_FORMAT;
}
